// /client/src/components/ContactView.js
import React, { useEffect } from 'react';
import Navigation from './Navigation';
import Footer from './Footer';
import ContactContent from './ContactContent';
import PageSEO from './PageSEO'; // Import the SEO component

const ContactView = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Comprehensive SEO configuration for contact page
  const contactSEO = {
    title: "Contact Kinder's Meats | BBQ Restaurant & Catering Inquiries California",
    description: "Contact Kinder's Meats Deli BBQ & Catering for restaurant questions, catering quotes, or feedback. Reach us for inquiries about our 7 California locations, BBQ catering services, menu information, and more. Multiple ways to get in touch with our family-owned BBQ restaurants.",
    keywords: [
      "contact Kinder's Meats",
      "BBQ restaurant contact",
      "California BBQ restaurant phone",
      "catering inquiry",
      "restaurant customer service",
      "BBQ catering quote",
      "restaurant feedback",
      "East Bay BBQ contact",
      "restaurant phone number",
      "BBQ restaurant locations contact",
      "Concord BBQ restaurant contact",
      "Brentwood BBQ restaurant contact", 
      "Martinez BBQ restaurant contact",
      "Pleasant Hill BBQ restaurant contact",
      "Walnut Creek BBQ restaurant contact",
      "Hercules BBQ restaurant contact",
      "restaurant general manager contact",
      "food service inquiry",
      "BBQ restaurant hours",
      "restaurant complaint",
      "restaurant compliment",
      "family restaurant contact",
      "California restaurant inquiry",
      "restaurant business hours"
    ],
    canonicalUrl: "/contact",
    ogImage: "https://kindersmeats.com/images/contact-kinders-meats-og.jpg",
    customMeta: {
      'contact:type': 'business',
      'contact:industry': 'Food Service',
      'contact:services': 'Restaurant, Catering, Deli',
      'contact:region': 'California, East Bay'
    },
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "ContactPage",
          "@id": "https://kindersmeats.com/contact#contactpage",
          "url": "https://kindersmeats.com/contact",
          "name": "Contact Kinder's Meats",
          "description": "Contact information for Kinder's Meats Deli BBQ & Catering - restaurant inquiries, catering quotes, and customer service",
          "mainEntity": {
            "@id": "https://kindersmeats.com/#organization"
          },
          "about": {
            "@id": "https://kindersmeats.com/#organization"
          }
        },
        {
          "@type": "Organization",
          "@id": "https://kindersmeats.com/#organization", 
          "name": "Kinder's Meats Deli BBQ & Catering",
          "alternateName": "Kinder's Meats",
          "url": "https://kindersmeats.com",
          "logo": {
            "@type": "ImageObject",
            "url": "https://kindersmeats.com/images/kinders-logo.png",
            "width": 300,
            "height": 100
          },
          "description": "Family-owned BBQ restaurant chain serving authentic barbecue, premium deli meats, and professional catering services across 7 California locations since 1946.",
          "foundingDate": "1946",
          "contactPoint": [
            {
              "@type": "ContactPoint",
              "contactType": "customer service",
              "availableLanguage": ["English"],
              "areaServed": {
                "@type": "State", 
                "name": "California"
              },
              "hoursAvailable": {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": [
                  "Monday",
                  "Tuesday", 
                  "Wednesday",
                  "Thursday",
                  "Friday",
                  "Saturday",
                  "Sunday"
                ],
                "opens": "08:30",
                "closes": "21:00"
              }
            },
            {
              "@type": "ContactPoint",
              "contactType": "catering",
              "availableLanguage": ["English"],
              "areaServed": {
                "@type": "State",
                "name": "California"
              },
              "description": "Professional BBQ catering services for events, corporate functions, and private parties"
            },
            {
              "@type": "ContactPoint", 
              "contactType": "customer support",
              "availableLanguage": ["English"],
              "areaServed": {
                "@type": "State",
                "name": "California"
              }
            }
          ],
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Contact Services",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "General Inquiries",
                  "description": "Questions about restaurants, menu, locations, and services"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service", 
                  "name": "Catering Consultation",
                  "description": "Professional catering quotes and event planning"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Customer Feedback",
                  "description": "Share your experience and suggestions"
                }
              }
            ]
          },
          "location": [
            {
              "@type": "Restaurant",
              "name": "Kinder's Meats Brentwood",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Brentwood",
                "addressRegion": "CA",
                "addressCountry": "US"
              }
            },
            {
              "@type": "Restaurant",
              "name": "Kinder's Meats Concord", 
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Concord",
                "addressRegion": "CA",
                "addressCountry": "US"
              }
            },
            {
              "@type": "Restaurant",
              "name": "Kinder's Meats Hercules",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Hercules",
                "addressRegion": "CA", 
                "addressCountry": "US"
              }
            },
            {
              "@type": "Restaurant",
              "name": "Kinder's Meats Martinez",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Martinez",
                "addressRegion": "CA",
                "addressCountry": "US"
              }
            },
            {
              "@type": "Restaurant",
              "name": "Kinder's Meats Pleasant Hill",
              "address": {
                "@type": "PostalAddress", 
                "addressLocality": "Pleasant Hill",
                "addressRegion": "CA",
                "addressCountry": "US"
              }
            },
            {
              "@type": "Restaurant",
              "name": "Kinder's Meats Walnut Creek",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Walnut Creek", 
                "addressRegion": "CA",
                "addressCountry": "US"
              }
            }
          ],
          "openingHours": [
            "Mo 08:30-21:00",
            "Tu 08:30-21:00", 
            "We 08:30-21:00",
            "Th 08:30-21:00",
            "Fr 08:30-21:00",
            "Sa 08:30-21:00",
            "Su 08:30-21:00"
          ],
          "priceRange": "$$",
          "acceptsReservations": false,
          "servesCuisine": ["BBQ", "American", "Deli"]
        },
        {
          "@type": "WebPage",
          "@id": "https://kindersmeats.com/contact#webpage",
          "url": "https://kindersmeats.com/contact",
          "name": "Contact Kinder's Meats | BBQ Restaurant & Catering Inquiries California",
          "description": "Contact Kinder's Meats for restaurant questions, catering quotes, or feedback about our 7 California BBQ locations.",
          "isPartOf": {
            "@id": "https://kindersmeats.com/#website"
          },
          "about": {
            "@id": "https://kindersmeats.com/#organization"
          },
          "mainEntity": {
            "@id": "https://kindersmeats.com/contact#contactpage"
          },
          "breadcrumb": {
            "@id": "https://kindersmeats.com/contact#breadcrumb"
          },
          "potentialAction": [
            {
              "@type": "CommunicateAction",
              "name": "Contact Kinder's Meats",
              "target": {
                "@type": "EntryPoint", 
                "urlTemplate": "https://kindersmeats.com/contact",
                "actionPlatform": ["http://schema.org/DesktopWebPlatform", "http://schema.org/MobileWebPlatform"]
              }
            }
          ]
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://kindersmeats.com/contact#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://kindersmeats.com"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Contact",
              "item": "https://kindersmeats.com/contact"
            }
          ]
        },
        {
          "@type": "FAQPage", 
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How can I contact Kinder's Meats?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "You can contact Kinder's Meats through our website contact form, visit any of our 7 California locations, or call your nearest location directly. We're here to help with restaurant questions, catering inquiries, and feedback."
              }
            },
            {
              "@type": "Question",
              "name": "What are your business hours?", 
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Most Kinder's locations are open Monday through Sunday from 8:30 AM to 9:00 PM. Hours may vary by location, so please check your specific location page for exact hours."
              }
            },
            {
              "@type": "Question",
              "name": "How do I request a catering quote?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "For catering inquiries, you can use our contact form and select 'Catering' as your inquiry type, or visit our catering page to learn more about our BBQ catering services for events and corporate functions."
              }
            },
            {
              "@type": "Question",
              "name": "Which locations do you have?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Kinder's Meats has 7 California locations: Brentwood, Concord, Hercules, Martinez, Pleasant Hill, and Walnut Creek. Visit our locations page for addresses and specific contact information."
              }
            },
            {
              "@type": "Question",
              "name": "Can I provide feedback about my experience?",
              "acceptedAnswer": {
                "@type": "Answer", 
                "text": "Absolutely! We value your feedback. Use our contact form to share compliments, suggestions, or concerns about your experience at any Kinder's location. Your input helps us maintain our high standards."
              }
            }
          ]
        }
      ]
    }
  };

  return (
    <>
      {/* SEO Component */}
      <PageSEO {...contactSEO} />
      
      {/* Main content with semantic HTML structure */}
      <main>
        <section className="theme-light m-0 p-0" data-theme="light">
          <div className='is-flex is-flex-direction-column fullHeight is-justify-content-space-between'>
            <div className='is-flex is-flex-direction-column is-justify-content-start'>
              <Navigation 
                colorMode="colorWhite"
              />

              {/* Contact Content */}
              <article itemScope itemType="https://schema.org/ContactPage">
                <ContactContent 
                    className="content-space-160 is-12-mobile is-10-tablet is-10-desktop is-10-widescreen is-8-fullhd is-offset-0-mobile is-offset-1-tablet is-offset-1-desktop is-offset-1-widescreen is-offset-2-fullhd"
                    title="Contact"
                />
              </article>
            </div>   

            {/* Hidden FAQ Section for SEO */}
            <section className="faq-section" style={{ display: 'none' }} aria-hidden="true" itemScope itemType="https://schema.org/FAQPage">
              <h2>Contact Information FAQ</h2>
              
              <div itemScope itemType="https://schema.org/Question">
                <h3 itemProp="name">How can I contact Kinder's Meats?</h3>
                <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                  <div itemProp="text">
                    You can contact Kinder's Meats through our website contact form, visit any of our 7 California locations, or call your nearest location directly. We're here to help with restaurant questions, catering inquiries, and feedback.
                  </div>
                </div>
              </div>

              <div itemScope itemType="https://schema.org/Question">
                <h3 itemProp="name">What are your business hours?</h3>
                <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                  <div itemProp="text">
                    Most Kinder's locations are open Monday through Sunday from 8:30 AM to 9:00 PM. Hours may vary by location, so please check your specific location page for exact hours.
                  </div>
                </div>
              </div>

              <div itemScope itemType="https://schema.org/Question">
                <h3 itemProp="name">How do I request a catering quote?</h3>
                <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                  <div itemProp="text">
                    For catering inquiries, you can use our contact form and select 'Catering' as your inquiry type, or visit our catering page to learn more about our BBQ catering services for events and corporate functions.
                  </div>
                </div>
              </div>

              <div itemScope itemType="https://schema.org/Question">
                <h3 itemProp="name">Which locations do you have?</h3>
                <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                  <div itemProp="text">
                    Kinder's Meats has 7 California locations: Brentwood, Concord, Hercules, Martinez, Pleasant Hill, and Walnut Creek. Visit our locations page for addresses and specific contact information.
                  </div>
                </div>
              </div>

              <div itemScope itemType="https://schema.org/Question">
                <h3 itemProp="name">Can I provide feedback about my experience?</h3>
                <div itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
                  <div itemProp="text">
                    Absolutely! We value your feedback. Use our contact form to share compliments, suggestions, or concerns about your experience at any Kinder's location. Your input helps us maintain our high standards.
                  </div>
                </div>
              </div>
            </section>

            <Footer />
          </div>
        </section>
      </main>
    </>
  );
};

export default ContactView;